import { AuctionTypeEnum, ThumbnailAttachmentTypeEnum } from '@on-arte/core-types';
import { 
  ArtworkMosaicItemDetails,
  getPathWithParams, 
  InfinitePaginationDetails, 
  ItemsMosaic, 
  ItemsMosaicTheme, 
  LoadingSpinner,
  useInfinitePagination,
  useLogger,
  UseLogger,
  useNotifications,
  UseNotifications,
  useRedirect,
  UseRedirect,
  UseState 
} from '@on-arte/ui';
import React, { useEffect, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQuery, useQueryClient } from 'react-query';

import { getCollection } from '@onArte/frontend/api/requests';
import { FrontendResponseCode, QueryKey } from '@onArte/frontend/enums';
import { useRouteInfo } from '@onArte/frontend/hooks';
import { RoutingState } from '@onArte/frontend/interfaces';
import { FrontendApiError } from '@onArte/frontend/models';
import { emptyRequest, getImageThumbnail } from '@onArte/frontend/utils';
import { RouteNameEnum } from '@onArte/shared/enums';
import { Collection, ItemWithAuctionDetails } from '@onArte/shared/interfaces/api';
import { buildRouteTitle, getRouteDetailsByName } from '@onArte/shared/utils';

import { 
  Container, 
  DetailsContainer,
  StyledArtworkDescription, 
  BackgroundContainer,
  BackgroundTitle,
  BackgroundImage,
  BackgroundContent,
  BackgroundOverlay,
  BackgroundSubtitle,
  NoArtworksMessage,
  MosaicWrapper,
  Wrapper
} from './collection.styled';

type MosaicItemMapper = (item: ItemWithAuctionDetails) => ArtworkMosaicItemDetails;

export const CollectionView: React.FC = (): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const { 
    setMaxItems, 
    hasMoreItems, 
    fetchMoreData, 
    paginationOffset, 
    initialItemsCount,
  }: InfinitePaginationDetails = useInfinitePagination(20);
  const { addToast }: UseNotifications = useNotifications();
  const { logger }: UseLogger = useLogger();
  const { elementParamsFromRouting, currentRouteObject }: RoutingState = useRouteInfo();
  const { redirect }: UseRedirect = useRedirect();

  const mapItemToMosaic: MosaicItemMapper = (item: ItemWithAuctionDetails): ArtworkMosaicItemDetails => ({
    image: getImageThumbnail(item.attachment, ThumbnailAttachmentTypeEnum.Size_340xAuto),
    boxTitleDetails: {
      itemName: item.name,
      author: item.manufacturer.name,
      price: item.auctionType === AuctionTypeEnum.PriceProposal ? undefined : item.price,
      biddersNumber: item.auctionType === AuctionTypeEnum.Bidding ? item.offers : undefined,
      bottomButtonLabel: item.auctionType === AuctionTypeEnum.PriceProposal ? t('onarte.common.priceProposal') : undefined,
      itemDescriptiveAttributes: item.label
        ? [t(item.label)]
        : [],
    },
    action: !item.auctionId
      ? (): void => addToast({ content: t('onarte.common.mosaicItems.noAuction') })
      : undefined,
    internalPath: item.auctionId
      ? item.route?.prettyUrl
        ?? getPathWithParams(getRouteDetailsByName(RouteNameEnum.ArtworkDetails)?.url ?? '/', { id: item.auctionId })
      : undefined
  });

  // Widok trzyma wszystko w stanie wypełnianym w `onSuccess`, a ten przy renderowaniu
  // serwerowym się nie wykonuje — bez tego strona selekcji wychodziła z serwera z samym
  // menu i stopką. Klucz zależy wyłącznie od identyfikatora z adresu, więc serwer sieje
  // pod nim wprost (ssrQueries.helper.ts), a tu wchodzi jako stan początkowy.
  const ssrCollection: Collection | undefined = useQueryClient()
    .getQueryData<Collection>([QueryKey.Collection, elementParamsFromRouting.id]);
  const ssrMosaicItems: ArtworkMosaicItemDetails[] = (ssrCollection?.items ?? []).map(mapItemToMosaic);

  const [collectionDetails, setCollectionDetails]: UseState<Collection | null>
    = useState<Collection | null>(ssrCollection ?? null);
  const [slicedMosaicItems, setSlicedMosaicItems]: UseState<ArtworkMosaicItemDetails[]>
    = useState<ArtworkMosaicItemDetails[]>(ssrMosaicItems.slice(0, initialItemsCount));
  const [allMosaicItems, setAllMosaicItems]: UseState<ArtworkMosaicItemDetails[]>
    = useState<ArtworkMosaicItemDetails[]>(ssrMosaicItems);

  useQuery(
    [QueryKey.Collection, elementParamsFromRouting.id],
    (): Promise<Collection | void> => elementParamsFromRouting.id && currentRouteObject?.name === RouteNameEnum.Collection
      ? getCollection(elementParamsFromRouting.id)
      : emptyRequest(),
    {
      onSuccess: (data: Collection | undefined): void => {
        if (data) {
          setCollectionDetails(data);
          setMaxItems(data.items.length);
          const allMosaicItemsArray: ArtworkMosaicItemDetails[] = data.items.map(mapItemToMosaic);
          setAllMosaicItems(allMosaicItemsArray);
          setSlicedMosaicItems(allMosaicItemsArray.slice(0, initialItemsCount));
        }
      },
      onError: (error: FrontendApiError): void => {
        logger(QueryKey.Collection, error);
        if (error.responseCode === FrontendResponseCode.NotFound) {
          redirect(getRouteDetailsByName(RouteNameEnum.NotFound)?.url ?? '/');
          return;
        } else {
          addToast({ content: t(error.message) });
        }
      }
    }
  );
  
  // Tytuł ustawia widok, bo `AppWrapper` celowo go nie dotyka na trasie selekcji —
  // patrz `routesManagingOwnTitle`. Bez tego przeglądarka po podłączeniu nadpisałaby
  // nazwę selekcji ogólnym „Selekcja" z definicji trasy.
  useEffect(
    (): void => {
      if (!collectionDetails) {
        return;
      }

      document.title = buildRouteTitle(collectionDetails.name);
    },
    [collectionDetails]
  );

  useEffect(
    (): void => {
      setMaxItems(allMosaicItems.length);
      setSlicedMosaicItems(allMosaicItems.slice(0, (initialItemsCount + paginationOffset)));
    },
    [paginationOffset]
  );

  return (
    <Container>
      {!!collectionDetails && (
        <Wrapper>
          <BackgroundContainer>
            <BackgroundContent>
              <BackgroundTitle as="h1">{collectionDetails.name}</BackgroundTitle>
              <BackgroundSubtitle>{collectionDetails.title}</BackgroundSubtitle>
            </BackgroundContent>
            <BackgroundImage src={collectionDetails.coverPhoto} alt={collectionDetails.name} />
            <BackgroundOverlay />
          </BackgroundContainer>
          <DetailsContainer>
            <StyledArtworkDescription
              title={t('onarte.website.collectionView.about.title')}
              description={collectionDetails.description ?? undefined}
              withReadMore 
            />
          </DetailsContainer>
          {slicedMosaicItems.length ? (
            <InfiniteScroll
              dataLength={slicedMosaicItems.length}
              next={fetchMoreData}
              hasMore={hasMoreItems}
              loader={
                <LoadingSpinner />
              }
              endMessage={false}
            >
              <MosaicWrapper id='collection-list'>
                <ItemsMosaic 
                  items={slicedMosaicItems}
                  theme={ItemsMosaicTheme.Mosaic}
                />
              </MosaicWrapper>
            </InfiniteScroll>
          ) : (
            <NoArtworksMessage>{t('onarte.website.collectionView.noArtworksMessage')}</NoArtworksMessage>
          )}
        </Wrapper>
      )}
    </Container>
  );
};
